import type { ObraDocument } from '@obras/shared';
import {
  extensionOf,
  FILE_KIND_META,
  formatBytes,
  formatDate,
  formatDateTime,
} from '@obras/shared';
import { FileText, History, Paperclip, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { Card, CardHeader } from '../../components/ui/Card.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { EmptyState, ErrorState, FormError, Skeleton } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { uploadMedia } from '../reports/api.js';
import { useCreateDocument, useDeleteDocument, useDocuments, useUpdateDocument } from './api.js';
import { BotaoBaixar, BotaoVer, Selo } from './Arquivo.js';

const ICON = { strokeWidth: 1.5 } as const;

/** O campo de arquivo: um botão que abre o seletor e mostra o escolhido. */
function EscolherArquivo({
  file,
  onChoose,
  label,
  vazio,
}: {
  file: File | null;
  onChoose: (escolhido: File | null) => void;
  label: string;
  vazio: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <span className="mb-1.5 block text-[13px] font-medium">{label}</span>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        onChange={(event) => onChoose(event.target.files?.[0] ?? null)}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex w-full items-center gap-3 rounded-md bg-muted px-4 py-3 text-left transition-colors hover:bg-accent"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-card">
          {file ? (
            <span className="font-mono text-[10px] tracking-wide text-muted-foreground uppercase">
              {extensionOf(file.name)}
            </span>
          ) : (
            <Paperclip {...ICON} size={16} className="text-muted-foreground" aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium">
            {file ? file.name : 'Escolher arquivo'}
          </span>
          <span className="block text-[12px] text-muted-foreground">
            {file ? formatBytes(file.size) : vazio}
          </span>
        </span>
      </button>
    </div>
  );
}

function Linha({
  documento,
  obraId,
  onEditar,
  onRemover,
  podeEditar,
}: {
  documento: ObraDocument;
  obraId: string;
  onEditar: () => void;
  onRemover: () => void;
  podeEditar: boolean;
}) {
  const [historico, setHistorico] = useState(false);

  return (
    <li className="px-5 py-3">
      <div className="flex items-center gap-3">
        <Selo extension={documento.extension} />

        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-medium">{documento.title}</p>
          {documento.report ? (
            <p className="truncate text-[13px] text-muted-foreground">
              Enviado no relatório {documento.report.title}, visita de{' '}
              {formatDate(documento.report.visitDate)}
            </p>
          ) : documento.description ? (
            <p className="truncate text-[13px] text-muted-foreground">{documento.description}</p>
          ) : null}
          <p className="truncate text-[12px] text-muted-foreground">
            {formatBytes(documento.sizeBytes)}
            {documento.uploadedBy ? ` · ${documento.uploadedBy.name}` : ''}
            {` · ${formatDateTime(documento.createdAt)}`}
          </p>
        </div>

        {documento.previousVersions.length > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<History {...ICON} size={14} />}
            onClick={() => setHistorico((aberto) => !aberto)}
          >
            {documento.previousVersions.length === 1
              ? '1 versão anterior'
              : `${documento.previousVersions.length} versões anteriores`}
          </Button>
        ) : null}

        <BotaoVer
          url={documento.url}
          extension={documento.extension}
          originalName={documento.originalName}
        />

        {/*
         * Baixar passa pela API, e não pelo CDN: é de lá que o arquivo sai com
         * o nome que tinha e como anexo. Ver o comentário da rota.
         */}
        <BotaoBaixar
          obraId={obraId}
          documentId={documento.id}
          originalName={documento.originalName}
        />

        {/*
         * O arquivo que veio de um relatório não se edita nem se retira aqui:
         * quem responde por ele é o relatório. Sem os botões, a linha diz isso
         * sozinha.
         */}
        {podeEditar && !documento.report ? (
          <>
            <Button
              size="icon"
              variant="ghost"
              className="size-9"
              title={`Editar ${documento.title}`}
              aria-label={`Editar ${documento.title}`}
              onClick={onEditar}
            >
              <Pencil {...ICON} size={16} aria-hidden />
            </Button>

            <Button
              size="icon"
              variant="ghost"
              className="size-9"
              title={`Retirar ${documento.title}`}
              aria-label={`Retirar ${documento.title}`}
              onClick={onRemover}
            >
              <Trash2 {...ICON} size={16} aria-hidden />
            </Button>
          </>
        ) : null}
      </div>

      {historico ? (
        <ul className="mt-3 ml-15 flex flex-col gap-2 border-l border-border pl-4">
          {documento.previousVersions.map((versao) => (
            <li key={versao.id} className="flex items-center gap-3">
              <span className="min-w-0 flex-1">
                {/*
                 * Riscado e em vermelho: esta versão foi substituída e não vale
                 * mais. Ela continua acessível porque pode estar citada num
                 * relatório publicado — mas quem abre o histórico precisa ver,
                 * antes de clicar, que não é este o arquivo em vigor.
                 */}
                <span className="block truncate font-mono text-[12px] text-danger line-through">
                  {versao.originalName}
                </span>
                <span className="block text-[12px] text-muted-foreground">
                  {formatBytes(versao.sizeBytes)} · substituído em{' '}
                  {formatDateTime(versao.replacedAt)}
                </span>
              </span>

              <BotaoVer
                url={versao.url}
                extension={versao.extension}
                originalName={versao.originalName}
                pequeno
              />
              <BotaoBaixar
                obraId={obraId}
                documentId={versao.id}
                originalName={versao.originalName}
                pequeno
              />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * O mesmo formulário serve para anexar e para editar.
 *
 * Editando, o arquivo é opcional: sem escolher outro, só título e descrição
 * mudam. Escolhendo, o anterior **não é apagado** — vira versão anterior, e
 * continua acessível na lista.
 */
function AnexoModal({
  obraId,
  open,
  editando,
  onClose,
}: {
  obraId: string;
  open: boolean;
  editando: ObraDocument | null;
  onClose: () => void;
}) {
  const create = useCreateDocument(obraId);
  const update = useUpdateDocument(obraId);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pronto, setPronto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Carrega os valores do anexo em edição na primeira renderização com ele.
  if (open && !pronto) {
    setTitle(editando?.title ?? '');
    setDescription(editando?.description ?? '');
    setPronto(true);
  }

  function fechar(): void {
    setFile(null);
    setTitle('');
    setDescription('');
    setPronto(false);
    setFormError(null);
    onClose();
  }

  function escolher(escolhido: File | null): void {
    setFile(escolhido);
    setFormError(null);

    // O nome do arquivo já é um título razoável; quem quiser, troca.
    if (escolhido && title.trim() === '') {
      setTitle(escolhido.name.replace(/\.[^.]+$/, '').slice(0, 180));
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    if (!editando && !file) {
      setFormError('Escolha um arquivo.');
      return;
    }

    setEnviando(true);
    try {
      /*
       * O arquivo sobe pela rota de mídia, que conhece a política de extensões
       * e o limite do órgão; só então é vinculado à obra. Separar assim é o que
       * permite recusar o arquivo antes de criar um registro pela metade.
       */
      const mediaId = file ? (await uploadMedia(file, 'documentos')).id : undefined;

      if (editando) {
        await update.mutateAsync({
          documentId: editando.id,
          body: {
            title: title.trim(),
            description: description.trim() === '' ? undefined : description.trim(),
            ...(mediaId ? { mediaId } : {}),
          },
        });
      } else if (mediaId) {
        await create.mutateAsync({
          mediaId,
          title: title.trim(),
          ...(description.trim() === '' ? {} : { description: description.trim() }),
        });
      }

      fechar();
    } catch (erro) {
      setFormError(erro instanceof ApiError ? erro.message : 'Não foi possível enviar o arquivo.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={editando ? 'Editar anexo' : 'Anexar arquivo'}
      description={
        editando ? 'Trocar o arquivo guarda o anterior como versão.' : 'Anexos relacionados à obra.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={fechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="anexo-form"
            type="submit"
            loading={enviando}
            icon={<Upload {...ICON} size={16} />}
          >
            {editando ? 'Salvar' : 'Anexar'}
          </Button>
        </>
      }
    >
      <form
        id="anexo-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError>{formError}</FormError>

        {editando ? (
          <div className="flex items-center gap-3 rounded-md bg-muted px-4 py-3">
            <Selo extension={editando.extension} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">
                {editando.originalName}
              </span>
              <span className="block text-[12px] text-muted-foreground">Arquivo atual</span>
            </span>
          </div>
        ) : null}

        <EscolherArquivo
          file={file}
          onChoose={escolher}
          label={editando ? 'Substituir o arquivo' : 'Arquivo'}
          vazio={editando ? 'Deixe em branco para manter o atual' : 'Nenhum arquivo escolhido'}
        />

        <TextField
          label="Título"
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />

        <TextAreaField
          label="Descrição"
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          hint="Opcional."
        />
      </form>
    </Modal>
  );
}

/**
 * Uma aba para documento e arquivo técnico.
 *
 * São a mesma coisa do ponto de vista do dossiê — o que muda é o formato —, e
 * duas abas obrigariam quem procura o memorial descritivo a adivinhar em qual
 * das duas ele foi parar. A separação acontece dentro, por seção, e sai da
 * extensão do arquivo: nenhuma escolha manual de quem anexa.
 */
export function ObraFilesTab({ obraId }: { obraId: string }) {
  const { can } = useAuth();
  const { data, isPending, isError, error, refetch } = useDocuments(obraId);
  const remove = useDeleteDocument(obraId);

  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<ObraDocument | null>(null);
  const [confirmar, setConfirmar] = useState<ObraDocument | null>(null);

  /*
   * A tela mostra os botões a quem provavelmente pode. Quem decide de verdade é
   * o servidor, que enxerga a designação da obra e responde 403 a quem não é da
   * equipe — aqui não há como saber isso sem uma consulta a mais.
   */
  const podeEditar = can('fiscal');

  const anexar = podeEditar ? (
    <Button variant="primary" icon={<Plus {...ICON} size={16} />} onClick={() => setNovo(true)}>
      Anexar arquivo
    </Button>
  ) : null;

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro desconhecido.'}
        onRetry={() => void refetch()}
      />
    );
  }

  if (isPending) return <Skeleton className="h-64 rounded-lg" />;

  return (
    <div className="flex flex-col gap-4">
      {data.total === 0 ? (
        <Card>
          <EmptyState
            icon={Paperclip}
            title="Nenhum anexo"
            description="Anexos da obra e arquivos enviados nos relatórios."
            {...(anexar ? { action: anexar } : {})}
          />
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <p className="text-[13px] text-muted-foreground">
              {data.total === 1 ? '1 anexo' : `${data.total} anexos`}
            </p>
            {anexar}
          </div>

          {data.groups.map((grupo) => (
            <Card key={grupo.kind}>
              <CardHeader
                title={FILE_KIND_META[grupo.kind].label}
                description={FILE_KIND_META[grupo.kind].hint}
              />
              <ul className="divide-y divide-border">
                {grupo.items.map((documento) => (
                  <Linha
                    key={documento.id}
                    documento={documento}
                    obraId={obraId}
                    podeEditar={podeEditar}
                    onEditar={() => setEditando(documento)}
                    onRemover={() => setConfirmar(documento)}
                  />
                ))}
              </ul>
            </Card>
          ))}
        </>
      )}

      <AnexoModal
        obraId={obraId}
        open={novo || editando !== null}
        editando={editando}
        onClose={() => {
          setNovo(false);
          setEditando(null);
        }}
      />

      <Modal
        open={confirmar !== null}
        onClose={() => setConfirmar(null)}
        title="Retirar do dossiê"
        description="O anexo sai da lista, com todas as suas versões. O histórico e a auditoria são preservados."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => {
                if (!confirmar) return;
                remove.mutate(confirmar.id, { onSuccess: () => setConfirmar(null) });
              }}
            >
              Retirar
            </Button>
          </>
        }
      >
        <p className="flex items-center gap-3 rounded-lg bg-muted p-4 text-[13px]">
          <FileText {...ICON} size={16} className="shrink-0 text-muted-foreground" aria-hidden />
          {confirmar?.title}
        </p>
      </Modal>
    </div>
  );
}
